import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Bell, AlertCircle, Info, AlertTriangle, Trash2, Plus, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime as formatDate } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_META = {
  high: { label: 'High', color: 'bg-red-100 text-red-700', icon: AlertCircle, ring: 'border-red-200' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle, ring: 'border-yellow-200' },
  low: { label: 'Low', color: 'bg-blue-100 text-blue-700', icon: Info, ring: 'border-blue-200' },
};


export const Announcements = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'management';

  const [announcements, setAnnouncements] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
    priority: 'medium',
    target_properties: [], // empty = all properties
  });

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const requests = [axios.get(`${API}/announcements`, { withCredentials: true })];
      if (isAdmin) {
        requests.push(axios.get(`${API}/properties`, { withCredentials: true }));
      }
      const results = await Promise.all(requests);
      setAnnouncements(results[0].data || []);
      if (isAdmin && results[1]) {
        const props = results[1].data || [];
        props.sort((a, b) => {
          const aNum = parseInt((a.name.match(/\d+/) || [0])[0], 10);
          const bNum = parseInt((b.name.match(/\d+/) || [0])[0], 10);
          return aNum - bNum;
        });
        setProperties(props);
      }
    } catch (e) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ title: '', content: '', priority: 'medium', target_properties: [] });
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/announcements`, form, { withCredentials: true });
      toast.success('Announcement published');
      resetForm();
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/announcements/${id}`, { withCredentials: true });
      toast.success('Announcement deleted');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const togglePropertyTarget = (propId) => {
    setForm(prev => {
      const has = prev.target_properties.includes(propId);
      return {
        ...prev,
        target_properties: has
          ? prev.target_properties.filter(p => p !== propId)
          : [...prev.target_properties, propId],
      };
    });
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Announcements</h1>
          <p className="text-sm text-[#64748b]">
            {isAdmin
              ? 'Publish updates, notices and alerts to residents.'
              : 'Community updates and notices from management.'}
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setShowForm(v => !v)}
            className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
            data-testid="toggle-new-announcement-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            {showForm ? 'Close form' : 'New Announcement'}
          </Button>
        )}
      </div>

      {/* Create form (admin only) */}
      {isAdmin && showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="new-announcement-form-card">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-announcement-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Scheduled water outage on Sunday"
                data-testid="announcement-title-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Content *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Details residents should know..."
                rows={4}
                data-testid="announcement-content-input"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Priority</Label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  data-testid="announcement-priority-select"
                >
                  <option value="low">Low — general info</option>
                  <option value="medium">Medium — notice</option>
                  <option value="high">High — urgent</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Target Properties</Label>
                <p className="text-xs text-[#64748b]">Leave all unchecked to broadcast to <strong>all properties</strong>.</p>
              </div>
            </div>

            {/* Property checklist */}
            <div className="max-h-40 overflow-y-auto border border-[#e2e8f0] rounded-md p-3 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="announcement-properties-list">
              {properties.length === 0 && (
                <p className="text-xs text-[#64748b] col-span-full">No properties available.</p>
              )}
              {properties.map(p => (
                <label
                  key={p._id}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#f8fafc] rounded px-2 py-1"
                  data-testid={`property-checkbox-${p._id}`}
                >
                  <input
                    type="checkbox"
                    checked={form.target_properties.includes(p._id)}
                    onChange={() => togglePropertyTarget(p._id)}
                    className="h-4 w-4 rounded border-[#e2e8f0]"
                  />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-[#e2e8f0]">
              <Button type="button" variant="outline" onClick={resetForm} data-testid="cancel-announcement-button">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-announcement-button">
                {submitting ? 'Publishing...' : 'Publish Announcement'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Announcements list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-white rounded-lg border border-[#e2e8f0] animate-pulse" />
          ))}
        </div>
      ) : announcements.length === 0 ? (
        <Card className="p-12 text-center bg-white border border-[#e2e8f0]" data-testid="no-announcements-empty">
          <Bell className="h-12 w-12 text-[#cbd5e1] mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>No announcements yet</h3>
          <p className="text-sm text-[#64748b]">
            {isAdmin ? 'Publish your first announcement using the button above.' : 'Check back soon for community updates.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => {
            const meta = PRIORITY_META[a.priority] || PRIORITY_META.low;
            const Icon = meta.icon;
            const targetLabels = (a.target_properties || []).map(pid => {
              const p = properties.find(x => x._id === pid);
              return p ? p.name : pid;
            });
            return (
              <Card
                key={a._id}
                className={`p-5 bg-white border ${meta.ring} hover:shadow-sm transition-shadow`}
                data-testid={`announcement-card-${a._id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }} data-testid={`announcement-title-${a._id}`}>{a.title}</h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${meta.color}`}>{meta.label}</span>
                        {isAdmin && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(a._id)}
                            className="text-[#64748b] hover:text-red-600 h-8 w-8 p-0"
                            data-testid={`delete-announcement-${a._id}`}
                            aria-label="Delete announcement"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-[#334155] mt-1 whitespace-pre-wrap" data-testid={`announcement-content-${a._id}`}>{a.content}</p>
                    <div className="flex items-center gap-3 text-xs text-[#64748b] mt-3 flex-wrap">
                      <span>By {a.created_by || 'Management'}</span>
                      <span>·</span>
                      <span>{formatDate(a.created_at)}</span>
                      {targetLabels.length > 0 && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {targetLabels.join(', ')}
                          </span>
                        </>
                      )}
                      {targetLabels.length === 0 && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            All properties
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Announcements;
